import { cacheBustedUrl } from 'utils/dynamicData';
import { isPlaceholder } from './movie';

/**
 * Normalize CMS movie data to match frontend expectations
 * Transforms nested CMS structure to flat frontend format
 *
 * CMS structure: { id, slug, content: { title, synopsis, hero, ... } }
 * Frontend expects: { id, slug, title, synopsis, hero, ... }
 *
 * @param {Object} movie - Raw movie data from CMS
 * @returns {Object} Normalized movie data
 */
export function normalizeCMSMovieData(movie) {
	if (!movie) return null;

	// CMS wraps all data in a 'content' object - unwrap it first
	let workingData = movie;
	if (movie.content && typeof movie.content === 'object') {
		workingData = {
			...movie.content,
			id: movie.id || movie.movie_id, // Preserve id at root level
			slug: movie.slug || movie.content.slug,
			published_status: movie.published_status
		};
	}

	const normalized = {
		...workingData,
		id: movie.id || movie.movie_id,
		placeholder: isPlaceholder(workingData),
	};

	// Extract synopsis from pages.synopsis.synopsis if not at root
	if (!normalized.synopsis && workingData.pages?.synopsis?.synopsis) {
		normalized.synopsis = workingData.pages.synopsis.synopsis;
	}

	// Extract and transform credits from pages.synopsis.credits
	// Check if credits don't exist OR are empty array
	if ((!normalized.credits || normalized.credits.length === 0) && workingData.pages?.synopsis?.credits) {
		const creditsData = workingData.pages.synopsis.credits;
		normalized.credits = [];

		// Credits structure: { cast: {credits: {...}, order: 1}, directors: {credits: {...}, order: 2}, ... }
		// Transform to array: [{type: "Cast", value: "Names"}, ...] sorted by section order

		// Get all sections and sort by their order field
		const sections = Object.entries(creditsData)
			.map(([key, section]) => ({ key, ...section }))
			.sort((a, b) => (a.order || 0) - (b.order || 0));

		sections.forEach(section => {
			if (section?.credits) {
				const creditsList = Object.values(section.credits);

				const names = creditsList
					.sort((a, b) => (a.order || 0) - (b.order || 0))
					.map(c => c.name)
					.filter(Boolean)
					.join(', ');

				if (names) {
					// All sections now use 'label' field consistently
					const type = section.label || section.sectionTitle || section.key.charAt(0).toUpperCase() + section.key.slice(1);
					normalized.credits.push({
						type: type,
						value: names
					});
				}
			}
		});
	}

	// Onesheet: Ensure proper format (object with url/alt)
	if (workingData.onesheet) {
		if (typeof workingData.onesheet === 'string') {
			normalized.onesheet = cacheBustedUrl(workingData.onesheet);
		} else if (workingData.onesheet.url) {
			const onesheetUrl = cacheBustedUrl(workingData.onesheet.url);
			normalized.onesheet = {
				url: onesheetUrl,
				alt: workingData.onesheet.alt || `Poster for ${workingData.title || 'movie'}`
			};
		}
	}

	// Hero: Keep object format and add heroImage fallback
	if (workingData.hero?.still?.url) {
		const heroUrl = cacheBustedUrl(workingData.hero.still.url);

		// Preserve focalPoint but ensure alt and url are set correctly
		const altText = workingData.hero.still.alt || `Hero image for ${workingData.title || 'movie'}`;

		normalized.hero = {
			...workingData.hero,
			still: {
				url: heroUrl,
				alt: altText,
				focalPoint: workingData.hero.still.focalPoint
			}
		};
		normalized.heroImage = heroUrl;
	} else if (workingData.heroImage && !workingData.hero) {
		// Add cache buster in preview mode using timestamp
		const heroUrl = cacheBustedUrl(workingData.heroImage);

		normalized.hero = {
			still: {
				url: heroUrl,
				alt: `Hero image for ${workingData.title || 'movie'}`
			}
		};
		normalized.heroImage = heroUrl;
	}

	// Transform trailers from object to array
	if (workingData.trailers && typeof workingData.trailers === 'object' && !Array.isArray(workingData.trailers)) {
		normalized.trailers = Object.values(workingData.trailers)
			.sort((a, b) => (a.order || 0) - (b.order || 0))
			.filter(t => t?.url)
			.map(t => ({
				url: t.url,
				title: t.title,
				thumbnailAlt: t.thumbnailAlt
			}));
	}

	// Transform genres
	if (workingData.genre && !workingData.genres) {
		normalized.genres = {
			base: [workingData.genre],
			essence: [workingData.genre],
			magic: [workingData.genre]
		};
	}

	// Ensure credits array exists (only if not already set by pages.synopsis.credits extraction)
	if (!normalized.credits || !Array.isArray(normalized.credits)) {
		// Check if there are credits at root level (non-nested structure)
		if (workingData.credits && Array.isArray(workingData.credits)) {
			// Credits already in correct format at root level
			normalized.credits = workingData.credits;
		} else {
			// No credits found anywhere, set empty array
			normalized.credits = [];
		}
	}

	// -- SHOWTIMES --
	if (normalized.showtimesBanner == null) normalized.showtimesBanner = !!workingData.pages?.showtimes?.banner;
	if (!Array.isArray(normalized.showtimesId)) normalized.showtimesId = workingData.showtimesId ? [workingData.showtimesId] : [];
	if (!normalized.showtimesBannerImage) normalized.showtimesBannerImage = workingData.pages?.showtimes?.banner?.image;
	if (!normalized.showtimesBannerLink) normalized.showtimesBannerLink = workingData.pages?.showtimes?.banner?.link;
	if (!normalized.showtimesBannerAlt) normalized.showtimesBannerAlt = workingData.pages?.showtimes?.banner?.alt;
	if (!normalized.showtimesBannerText) normalized.showtimesBannerText = workingData.pages?.showtimes?.banner?.text;
	if (!normalized.showtimesBannerDisplayType) normalized.showtimesBannerDisplayType = workingData.pages?.showtimes?.banner?.type;
	if (!normalized.showtimesQuickFilters) normalized.showtimesQuickFilters = {};
	if (!normalized.showtimesQuickFilters.title) normalized.showtimesQuickFilters.title = workingData.pages?.showtimes?.quickFiltersText;
	if (!normalized.showtimesQuickFilters.formatOptions) normalized.showtimesQuickFilters.formatOptions = workingData.pages?.showtimes?.quickFilters || {};

	// -- HOMEENT --
	if (!normalized.homeEnt) normalized.homeEnt = workingData.pages?.homeEnt;
	if (normalized.homeEntBanner == null) normalized.homeEntBanner = !!workingData.pages?.homeEnt?.banner;
	if (!normalized.homeEntBannerImage) normalized.homeEntBannerImage = workingData.pages?.homeEnt?.banner?.image;
	if (!normalized.homeEntBannerLink) normalized.homeEntBannerLink = workingData.pages?.homeEnt?.banner?.link;
	if (!normalized.homeEntBannerAlt) normalized.homeEntBannerAlt = workingData.pages?.homeEnt?.banner?.alt;
	if (!normalized.homeEntBannerText) normalized.homeEntBannerText = workingData.pages?.homeEnt?.banner?.text;
	if (!normalized.homeEntBannerDisplayType) normalized.homeEntBannerDisplayType = workingData.pages?.homeEnt?.banner?.type;

	return normalized;
}
